import { supabase } from "@/integrations/supabase/client";

export const TRIAL_DAYS = 3;
export const PREMIUM_MONTHLY_PRICE = "$9.99";

export type TrialStatus = "none" | "active" | "expired" | "converted" | "canceled";

export interface PremiumState {
  authenticated: boolean;
  premium_access: boolean;
  trial_status: TrialStatus;
  trial_used: boolean;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  trial_days_total: number;
  premium_offer_seen_at: string | null;
  subscription_status: string | null;
  plan_name: string | null;
  billing_platform: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  subscription_started_at: string | null;
  subscription_cancelled_at: string | null;
  subscription_expires_at: string | null;
  grace_period_ends_at: string | null;
  pro_bonus_until: string | null;
  /** Store platform behind the entitlement, when it came from Apple/Google. */
  store_platform: string | null;
  store_status: string | null;
  /** Apple/Google verified expiry (StoreKit `expiresDate`) — never client-computed. */
  store_expires_at: string | null;
}

const EMPTY: PremiumState = {
  authenticated: false,
  premium_access: false,
  trial_status: "none",
  trial_used: false,
  trial_started_at: null,
  trial_ends_at: null,
  trial_days_total: TRIAL_DAYS,
  premium_offer_seen_at: null,
  subscription_status: null,
  plan_name: null,
  billing_platform: "stripe",
  cancel_at_period_end: false,
  current_period_end: null,
  subscription_started_at: null,
  subscription_cancelled_at: null,
  subscription_expires_at: null,
  grace_period_ends_at: null,
  pro_bonus_until: null,
  store_platform: null,
  store_status: null,
  store_expires_at: null,
};

/** Server-computed premium/trial state — the client never decides entitlement itself. */
export async function fetchPremiumState(): Promise<PremiumState> {
  const { data, error } = await supabase.rpc("my_premium_state");
  if (error) {
    console.error("my_premium_state failed:", error.message);
    return EMPTY;
  }
  const raw = (data ?? {}) as Partial<PremiumState>;
  return { ...EMPTY, ...raw, trial_days_total: raw.trial_days_total ?? TRIAL_DAYS };
}

/**
 * NOTE: there is intentionally no card-free trial grant here. A Premium trial
 * is always the introductory period of the recurring monthly subscription and
 * is started through `startTrialWithPaymentMethod` in `@/lib/trial`.
 */

export async function markPremiumOfferSeen(): Promise<void> {
  const { error } = await supabase.rpc("mark_premium_offer_seen");
  if (error) console.error("mark_premium_offer_seen failed:", error.message);
}

export interface TrialProgress {
  active: boolean;
  dayNumber: number;
  daysTotal: number;
  hoursRemaining: number;
  daysRemaining: number;
  percent: number;
  label: string;
}

export function trialProgress(state: Pick<PremiumState, "trial_status" | "trial_started_at" | "trial_ends_at" | "trial_days_total">): TrialProgress {
  const total = state.trial_days_total || TRIAL_DAYS;
  const start = state.trial_started_at ? new Date(state.trial_started_at).getTime() : null;
  const end = state.trial_ends_at ? new Date(state.trial_ends_at).getTime() : null;
  const now = Date.now();
  const active = state.trial_status === "active" && !!end && end > now;

  if (!active || !end) {
    return { active: false, dayNumber: 0, daysTotal: total, hoursRemaining: 0, daysRemaining: 0, percent: 100, label: "Trial ended" };
  }

  const spanMs = start ? Math.max(end - start, 1) : total * 86_400_000;
  const elapsed = Math.min(Math.max(now - (start ?? end - spanMs), 0), spanMs);
  const hoursRemaining = Math.max(0, Math.ceil((end - now) / 3_600_000));
  const daysRemaining = Math.max(1, Math.ceil(hoursRemaining / 24));
  const dayNumber = Math.min(total, Math.floor(elapsed / 86_400_000) + 1);

  const label =
    hoursRemaining <= 24
      ? hoursRemaining <= 2
        ? "Trial ends in under 2 hours"
        : "Trial Ends Tomorrow"
      : `${daysRemaining} Days Remaining`;

  return {
    active: true,
    dayNumber,
    daysTotal: total,
    hoursRemaining,
    daysRemaining,
    percent: Math.round((elapsed / spanMs) * 100),
    label,
  };
}

export const PREMIUM_BENEFITS = [
  "50 AI Tutor Questions Every Day + Full Premium Study Access.",
  "Homework Image Scanning",
  "AI Flashcards",
  "AI Quizzes",
  "AI Study Coach",
  "Premium Themes",
  "Double XP",
  "Priority Support",
] as const;

/** Celebration burst used when Premium begins. Lazy-loaded so it never blocks first paint. */
export async function celebratePremium(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const confetti = (await import("canvas-confetti")).default;
    const colors = ["#ff2d92", "#ffd400", "#7c3aed", "#22d3ee"];
    confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors });
    setTimeout(() => confetti({ particleCount: 60, spread: 100, origin: { y: 0.5 }, colors }), 250);
  } catch {
    /* confetti is decorative only */
  }
}